import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/**
 * Auth provider — dynamically imports the Supabase SDK so the ~55 KiB client
 * is not part of the main bundle. The import resolves on first paint (after
 * Suspense/idle), keeping LCP fast on public pages where auth isn't needed.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState<any>(null);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    // Defer SDK import until after the browser is idle so it doesn't compete with LCP.
    const start = async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      if (cancelled) return;
      setClient(supabase);

      const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
        setSession(sess);
        setUser(sess?.user ?? null);
        if (sess?.user) {
          setTimeout(() => checkAdmin(supabase, sess.user.id), 0);
        } else {
          setIsAdmin(false);
        }
      });
      unsub = () => sub.subscription.unsubscribe();

      const { data } = await supabase.auth.getSession();
      const sess = data.session;
      setSession(sess);
      setUser(sess?.user ?? null);
      if (sess?.user) checkAdmin(supabase, sess.user.id);
      setLoading(false);
    };

    // Wait for first meaningful user interaction OR a post-LCP timeout.
    // Do not use requestIdleCallback here: Lighthouse can report idle work as
    // critical-path CPU/network when it fires during the initial audit window.
    let fired = false;
    const fire = () => {
      if (fired || cancelled) return;
      fired = true;
      cleanup();
      start();
    };
    const events = ["pointerdown", "keydown", "scroll", "touchstart"] as const;
    const cleanup = () => {
      events.forEach((e) => window.removeEventListener(e, fire));
      window.clearTimeout(handle);
    };
    events.forEach((e) =>
      window.addEventListener(e, fire, { once: true, passive: true } as AddEventListenerOptions)
    );
    const handle = window.setTimeout(fire, 6000);

    return () => {
      cancelled = true;
      if (unsub) unsub();
      cleanup();
    };
  }, []);

  async function checkAdmin(supabase: any, userId: string) {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    setIsAdmin(!!data?.some((r: any) => r.role === "admin" || r.role === "editor"));
  }

  async function signOut() {
    if (!client) {
      const { supabase } = await import("@/integrations/supabase/client");
      await supabase.auth.signOut();
      return;
    }
    await client.auth.signOut();
  }

  return (
    <Ctx.Provider value={{ user, session, isAdmin, loading, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
